import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  Check as CheckIcon,
  ChevronDown as ChevronDownIcon,
  Search as SearchIcon,
  UserRound as UserIcon,
  UserRoundX as UnassignIcon,
} from "lucide-react";
import type { AssignableMemberDto, ConversationAssignedMemberDto } from "@multi-chat/shared";
import { useMinWidth } from "../../components/layout/use-min-width.js";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "../../components/ui/dropdown-menu/index.js";
import { Sheet, SheetContent, SheetTitle } from "../../components/ui/sheet/index.js";
import { cn } from "../../lib/utils.js";
import { useOptionalNotifications } from "../notifications/notification-context.js";

const TONES = [
  "bg-[#d7efe4] text-[#1d6b4f]",
  "bg-[#e4eef8] text-[#245a86]",
  "bg-[#f3ead8] text-[#7a5a22]",
  "bg-[#f8e4ea] text-[#8a3d55]",
] as const;

export function memberInitials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  const letters = parts.slice(0, 2).map((part) => part[0] ?? "");
  return letters.join("").toUpperCase() || "?";
}

export function MemberInitials({
  name,
  memberId,
  inactive = false,
  size = "sm",
}: {
  name: string;
  memberId: number;
  inactive?: boolean;
  size?: "sm" | "xs";
}): React.JSX.Element {
  const tone = TONES[Math.abs(memberId) % TONES.length] ?? TONES[0];
  return (
    <span
      className={cn(
        "inline-grid shrink-0 place-items-center rounded-full font-extrabold",
        size === "sm" ? "size-[22px] text-[10px]" : "size-4 text-[8px]",
        inactive ? "bg-inbox-line-soft text-inbox-muted" : tone,
      )}
      aria-hidden="true"
    >
      {memberInitials(name)}
    </span>
  );
}

type AssignResult = { ok: boolean; message?: string } | void;

export function AssigneePickerList({
  assignedMember,
  members,
  onAssign,
}: {
  assignedMember: ConversationAssignedMemberDto | null;
  members: readonly AssignableMemberDto[];
  onAssign: (memberId: number | null) => AssignResult | Promise<AssignResult>;
}): React.JSX.Element {
  const { t } = useTranslation();
  const notifications = useOptionalNotifications();
  const [query, setQuery] = useState("");
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    if (!needle) return members;
    return members.filter((member) => member.displayName.toLowerCase().includes(needle));
  }, [members, query]);

  function choose(memberId: number | null): void {
    void Promise.resolve(onAssign(memberId)).then((result) => {
      if (result && !result.ok && result.message) {
        notifications?.pushNotification({ message: result.message });
      }
    });
  }

  return (
    <div className="flex min-h-0 flex-col" data-testid="inbox-assignee-picker">
      <label className="flex items-center gap-2 border-b border-inbox-line-soft px-2.5 py-2">
        <SearchIcon size={14} className="text-inbox-muted" aria-hidden="true" />
        <input
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          onKeyDown={(event) => event.stopPropagation()}
          placeholder={t("inbox.workflow.assignee.search")}
          className="min-w-0 flex-1 bg-transparent text-[0.8125rem] text-inbox-strong outline-none placeholder:text-inbox-subtle"
          aria-label={t("inbox.workflow.assignee.search")}
        />
      </label>
      <div className="max-h-64 overflow-y-auto p-1.5" role="listbox" aria-label={t("inbox.workflow.assignee.label")}>
        {filtered.length === 0 ? (
          <p className="m-0 px-2 py-3 text-[0.8125rem] text-inbox-muted">{t("inbox.workflow.assignee.empty")}</p>
        ) : (
          filtered.map((member) => {
            const selected = assignedMember?.memberId === member.memberId;
            return (
              <button
                key={member.memberId}
                type="button"
                role="option"
                aria-selected={selected}
                className="flex min-h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-start text-[0.8125rem] hover:bg-inbox-hover"
                onClick={() => choose(member.memberId)}
              >
                <MemberInitials name={member.displayName} memberId={member.memberId} />
                <span className="min-w-0 flex-1 truncate">
                  {member.displayName}
                  {member.isCurrentMember ? (
                    <span className="text-inbox-muted"> {t("inbox.workflow.assignee.you")}</span>
                  ) : null}
                </span>
                {selected ? <CheckIcon size={16} className="shrink-0 text-primary" aria-hidden="true" /> : null}
              </button>
            );
          })
        )}
      </div>
      {assignedMember ? (
        <div className="border-t border-inbox-line-soft p-1.5">
          <button
            type="button"
            className="flex min-h-9 w-full items-center gap-2.5 rounded-lg px-2.5 text-start text-[0.8125rem] hover:bg-inbox-hover"
            data-testid="inbox-unassign"
            onClick={() => choose(null)}
          >
            <UnassignIcon size={16} className="text-inbox-muted" aria-hidden="true" />
            <span>{t("inbox.workflow.assignee.unassign")}</span>
          </button>
        </div>
      ) : null}
      {assignedMember && !assignedMember.isActive ? (
        <p className="m-0 px-2.5 pb-2 text-[0.75rem] text-inbox-muted">{t("inbox.workflow.assignee.inactive")}</p>
      ) : null}
    </div>
  );
}

export function ConversationAssigneeControl({
  assignedMember,
  members,
  pending,
  onAssign,
  compact = false,
}: {
  assignedMember: ConversationAssignedMemberDto | null;
  members: readonly AssignableMemberDto[];
  pending: boolean;
  onAssign: (memberId: number | null) => AssignResult | Promise<AssignResult>;
  compact?: boolean;
}): React.JSX.Element {
  const { t } = useTranslation();
  const isWide = useMinWidth(768);
  const [open, setOpen] = useState(false);
  const name = assignedMember?.displayName ?? t("inbox.workflow.assignee.unassigned");

  function choose(memberId: number | null) {
    setOpen(false);
    return onAssign(memberId);
  }

  const trigger = (
    <button
      type="button"
      disabled={pending}
      className={cn(
        "inline-flex h-8 max-w-[170px] items-center gap-1.5 rounded-[9px] border border-inbox-line bg-surface px-1.5 text-[13px] font-semibold text-[#2d4a42] hover:border-[#c9d9d4] hover:bg-[#fbfdfc] disabled:opacity-60",
        compact && "max-w-8 px-1",
      )}
      aria-haspopup={isWide ? "menu" : "dialog"}
      aria-expanded={open}
      aria-label={`${t("inbox.workflow.assignee.label")}: ${name}`}
      data-testid="inbox-assignee-trigger"
      onClick={isWide ? undefined : () => setOpen(true)}
    >
      {assignedMember ? (
        <MemberInitials
          name={assignedMember.displayName}
          memberId={assignedMember.memberId}
          inactive={!assignedMember.isActive}
        />
      ) : (
        <span className="inline-grid size-[22px] place-items-center rounded-full bg-inbox-line-soft text-inbox-muted">
          <UserIcon size={12} aria-hidden="true" />
        </span>
      )}
      {compact ? null : <span className="min-w-0 truncate">{name}</span>}
      {compact ? null : <ChevronDownIcon size={14} className="shrink-0 text-inbox-muted" aria-hidden="true" />}
    </button>
  );

  const list = (
    <AssigneePickerList assignedMember={assignedMember} members={members} onAssign={choose} />
  );

  if (!isWide) {
    return (
      <>
        {trigger}
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetContent side="bottom" className="max-h-[80dvh] gap-0 bg-surface p-0">
            <SheetTitle className="border-b border-inbox-line-soft px-4 py-3 text-[0.9375rem]">
              {t("inbox.workflow.assignee.label")}
            </SheetTitle>
            {list}
          </SheetContent>
        </Sheet>
      </>
    );
  }

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger asChild>{trigger}</DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 rounded-xl border-inbox-line p-0 shadow-inbox-pop">
        {list}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
